import React, {useState} from 'react';
import {
    Box,
    Button,
    Collapse,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
    Stack,
    Divider,
    Chip,
} from "@mui/material";
import {Delete, Edit, KeyboardArrowDown, KeyboardArrowRight, CalendarMonth, MoreHoriz, PostAdd} from "@mui/icons-material";
import formatNumber from "../../../../../helpers/formatNumber";
import floatParser from "../../../../../helpers/floatParser";
import {useSelector} from "../../../../../redux/store";
import LogbookApi from "../../../../../api/staffApi/logbookApi";

interface GunlukKayitlarTablosuProps {
    hesap: any[];
    kisiId: number;
    kisiHesapId: number;
    bilgileriGuncelle: (res: any) => void;
    setKayit: (kayit: any) => void;
    setKayitEkle: (val: boolean) => void;
    setKayitGuncelle: (val: boolean) => void;
    isProfil?: boolean;
    page: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (newPage: number) => void;
    onPageSizeChange: (newPageSize: number) => void;
}

const Row = (props: {
    row: any,
    toplamGun: number,
    toplamSaat: number,
    toplamUcret: number,
    isProfil: boolean,
    kisiHesapId: number,
    setKayit: (k: any) => void,
    setKayitGuncelle: (v: boolean) => void,
    bilgileriGuncelle: (res: any) => void
}) => {
    const {
        row,
        toplamGun,
        toplamSaat,
        toplamUcret,
        isProfil,
        kisiHesapId,
        setKayit,
        setKayitGuncelle,
        bilgileriGuncelle
    } = props;
    const [open, setOpen] = useState(false);
    const sabitler = useSelector(state => state.app.sabitler);

    const hasDetailedMoves = (row.yapilanHarcamalar?.length > 0) ||
        (row.hesaptanTransferler?.length > 0) ||
        (row.hesabaTransferler?.length > 0) ||
        (row.yapilanOdemeler?.length > 0) ||
        (row.aciklama);

    return (
        <React.Fragment>
            <TableRow sx={{
                '& > *': { borderBottom: 'unset' },
                bgcolor: row.duzeltme ? 'warning.light' : 'transparent',
                transition: 'background-color 0.2s',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.01)' }
            }}>
                <TableCell align="center">
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1}}>
                        {hasDetailedMoves && (
                            <IconButton size="small" onClick={() => setOpen(!open)} sx={{ color: 'primary.main' }}>
                                {open ? <KeyboardArrowDown/> : <KeyboardArrowRight/>}
                            </IconButton>
                        )}
                        {row.duzeltme && !isProfil && (
                            <Stack direction="row" spacing={0.5}>
                                <IconButton size="small" onClick={() => {
                                    setKayit({...row, hesapId: kisiHesapId});
                                    setKayitGuncelle(true);
                                }} sx={{ bgcolor: 'info.light', color: 'info.dark', '&:hover': { bgcolor: '#b3e5fc' } }}>
                                    <Edit fontSize="small"/>
                                </IconButton>
                                <IconButton size="small" color="error"
                                            onClick={() => LogbookApi.deleteHesapKayit({id: row.id}).then(res => bilgileriGuncelle(res))}
                                            sx={{ bgcolor: 'error.light', color: 'error.dark', '&:hover': { bgcolor: '#ffcdd2' } }}>
                                    <Delete fontSize="small"/>
                                </IconButton>
                            </Stack>
                        )}
                    </Box>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>{row.tarih}</TableCell>
                <TableCell align="center">
                    {(toplamGun > 0 || toplamSaat > 0) && (
                        <Chip label={`${toplamGun}G / ${toplamSaat}S`} size="small" sx={{ fontWeight: 700, bgcolor: 'primary.light', color: 'primary.dark' }} />
                    )}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(toplamUcret)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800, color: floatParser(row.degisim) >= 0 ? 'success.main' : 'error.main' }}>
                    {formatNumber(floatParser(row.degisim))}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 900 }}>{formatNumber(row.sonBakiye)}</TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{margin: 2, p: 2, bgcolor: 'grey.50', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.05)'}}>
                            <Typography variant="overline" fontWeight={800} color="textSecondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <MoreHoriz fontSize="small" /> Kayıt Detayları
                            </Typography>
                            <Grid container spacing={2}>
                                {row.aciklama && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" sx={{ p: 1.5, bgcolor: 'white', borderRadius: '8px', borderLeft: '4px solid', borderColor: 'primary.main' }}>
                                            <strong>Açıklama:</strong> {row.aciklama}
                                        </Typography>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <Stack spacing={1}>
                                        {row.yapilanHarcamalar?.map((h: any, i: number) => (
                                            <DetailRow key={i} label="Harcama" value={h.miktar} desc={sabitler?.gider_tipleri?.find((q: any) => q.id === h.tip)?.tip} color="error.main" />
                                        ))}
                                        {row.yapilanOdemeler?.map((h: any, i: number) => (
                                            <DetailRow key={i} label="Ödeme" value={h.miktar} desc={h.tip} color="success.main" />
                                        ))}
                                        {row.hesaptanTransferler?.map((h: any, i: number) => (
                                            <DetailRow key={i} label="Transfer (Giden)" value={h.miktar} desc={sabitler?.hesaplar?.find((q: any) => q.id === h.hedef)?.hesapAdi} color="error.main" />
                                        ))}
                                        {row.hesabaTransferler?.map((h: any, i: number) => (
                                            <DetailRow key={i} label="Transfer (Gelen)" value={h.miktar} desc={sabitler?.hesaplar?.find((q: any) => q.id === h.kaynak)?.hesapAdi} color="success.main" />
                                        ))}
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
};

const DetailRow = ({ label, value, desc, color }: any) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: 'white', borderRadius: '6px' }}>
        <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" fontWeight={800} sx={{ color }}>{label.toUpperCase()}</Typography>
            <Typography variant="body2" color="textSecondary">{desc}</Typography>
        </Stack>
        <Typography variant="body2" fontWeight={800}>{formatNumber(value)}</Typography>
    </Box>
);

const GunlukKayitlarTablosu: React.FC<GunlukKayitlarTablosuProps> = ({
                                                                         hesap,
                                                                         kisiId,
                                                                         kisiHesapId,
                                                                         bilgileriGuncelle,
                                                                         setKayit,
                                                                         setKayitEkle,
                                                                         setKayitGuncelle,
                                                                         isProfil = false,
                                                                         page,
                                                                         pageSize,
                                                                         totalCount,
                                                                         onPageChange,
                                                                         onPageSizeChange
                                                                     }) => {
    return (
        <Box sx={{ width: '100%' }}>
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ mb: 2.5, px: 1 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <CalendarMonth color="primary" />
                    <Typography variant="h6" fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Mesai ve Günlük Kayıtlar</Typography>
                </Stack>
                {!isProfil && (
                    <Button 
                        variant="contained" 
                        size="small" 
                        startIcon={<PostAdd />}
                        onClick={() => setKayitEkle(true)}
                        sx={{ borderRadius: '10px', fontWeight: 800 }}
                    >
                        Kayıt Ekle
                    </Button>
                )}
            </Stack>

            <TableContainer component={Paper} sx={{ borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: 'none' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell align="center" sx={{ width: 80 }}></TableCell>
                            <TableCell align="center" sx={{ fontWeight: 800 }}>Tarih</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 800 }}>Mesai</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>Hakediş</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>Değişim</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>Bakiye</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(hesap ?? []).map((row: any, index: number) => {
                            const toplamGun = row.mesailer?.reduce((sum: number, m: any) => sum + (m.tam_gun ? 1 : 0), 0) ?? 0;
                            const toplamSaat = row.mesailer?.reduce((sum: number, m: any) => sum + (m.saat ?? 0), 0) ?? 0;
                            const toplamUcret = row.mesailer?.reduce((sum: number, m: any) => sum + floatParser(m.ucret ?? 0), 0) ?? 0;

                            return (
                                <Row
                                    key={"hesap-" + index}
                                    row={row}
                                    toplamGun={toplamGun}
                                    toplamSaat={toplamSaat}
                                    toplamUcret={toplamUcret}
                                    isProfil={isProfil}
                                    kisiHesapId={kisiHesapId}
                                    setKayit={setKayit}
                                    setKayitGuncelle={setKayitGuncelle}
                                    bilgileriGuncelle={bilgileriGuncelle}
                                />
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={pageSize}
                page={page - 1}
                onPageChange={(_, p) => onPageChange(p + 1)}
                onRowsPerPageChange={(e) => onPageSizeChange(+e.target.value)}
                sx={{ mt: 1 }}
            />
        </Box>
    );
};

export default GunlukKayitlarTablosu;
